import { type FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedMarketId, EnhancedViewId } from '#api/enhancements/enhancedTmsApiTypes'
import { useGetMarketsQuery, useViewsByMarketIdQuery } from '#api/tmsApiHooks'
import { Icon } from '#components/Icon'
import { Select, type SelectEntry } from '#components/Select'
import { Spinner } from '#components/Spinner'
import WarningIcon from '#icons/warning-triangle-outline-16.svg'
import { testIds } from '#utils/testIds'
import { SelectionField } from './SelectionField'
import { TextInput } from './TextInput'

export const ViewInput: FC<{
  marketId?: EnhancedMarketId
  viewId?: EnhancedViewId
  onSelected: (viewId: EnhancedViewId) => void
  kind?: 'inline' | 'full'
  disabled?: boolean
}> = ({ marketId, viewId, onSelected, kind, disabled }) => {
  const { t } = useTranslation()
  const markets = useGetMarketsQuery()
  const isMarketsLoading = markets.isLoading || markets.isFetching
  const foundMarket = markets.data?.markets?.find((m) => m.marketId === marketId)
  const views = useViewsByMarketIdQuery({ marketId: marketId as EnhancedMarketId }, { skip: !foundMarket })

  const input = useMemo(() => {
    if (!foundMarket && !isMarketsLoading) {
      return (
        <section className="has-error">
          <Icon iconUrl={WarningIcon} />
          {!views.isError && <span>{t('setup.view.error.select-valid-market', 'Please select a valid market')}</span>}
          {views.isError && <span>{t('setup.view.error.failed-loading-views', 'Failed loading views')}</span>}
        </section>
      )
    }

    if (views.isFetching || isMarketsLoading) {
      return (
        <section className="split-flex">
          <Spinner size="s" />
          <span>{t('setup.view.loading', 'Loading views…')}</span>
        </section>
      )
    }

    const viewSelectItems = (views.data?.listOfViewHeader ?? [])
      .map(
        (v) =>
          ({
            id: v.viewID ?? ('unknown' as unknown as EnhancedViewId),
            value: `${v.viewName ?? t('setup.view.unknown')} (${v.viewID})`,
          }) satisfies SelectEntry<EnhancedViewId, string>,
      )
      .toSorted((a, b) => a.value.localeCompare(b.value))

    return (
      <Select
        label={kind === 'inline' ? undefined : t('setup.view.label', 'View')}
        options={viewSelectItems}
        value={viewId}
        placeholder={t('setup.view.unknown', 'Unknown View')}
        onChange={(item) => {
          if (item) {
            onSelected(item)
          }
        }}
        slim
        disabled={disabled}
      />
    )
  }, [t, viewId, foundMarket, onSelected, views, kind, isMarketsLoading, disabled])

  return (
    <SelectionField data-testid={testIds.setup.callingParameters.view}>
      {input}

      <TextInput
        type="number"
        value={viewId}
        onChange={(x) => {
          if (!Number.isNaN(x.target.valueAsNumber)) {
            onSelected(x.target.valueAsNumber as EnhancedViewId)
          }
        }}
        disabled={disabled}
      />
    </SelectionField>
  )
}
